import { z } from 'zod';
import { currencySchema, i18nTextSchema, imageSchema, moneySchema } from './common.js';
import { modifierSelectionSchema } from './verticals.js';

/**
 * Catalog contracts. CLAUDE.md §3: a base `product` plus per-vertical
 * extension tables. v1 ships exactly one extension — `product_retail_variant`
 * (size × colour, per-variant stock, per-variant image).
 */

export const productStatusSchema = z.enum(['draft', 'active', 'archived']);

export const categorySchema = z.object({
  id: z.string().uuid(),
  name: i18nTextSchema,
  slug: z.string(),
  position: z.number().int(),
  productCount: z.number().int().optional(),
});
export type CategoryDto = z.infer<typeof categorySchema>;

/**
 * Retail variant. `price` is nullable: most fashion shops price per product
 * and only override for, say, XXL. Stock is per variant and decremented
 * atomically at order time.
 */
export const retailVariantSchema = z.object({
  id: z.string().uuid(),
  sku: z.string().nullable(),
  size: z.string().max(30).nullable(),
  color: z.string().max(30).nullable(),
  colorHex: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable(),
  price: moneySchema.nullable(),
  stock: z.number().int(),
  imageUrl: z.string().url().nullable(),
  isActive: z.boolean(),
  position: z.number().int(),
});
export type RetailVariantDto = z.infer<typeof retailVariantSchema>;

/**
 * F&B extension as the storefront sees it.
 *
 * `isSoldOut` is separate from `status` on purpose (§4): a shop marks the
 * beef noodles sold out at 2pm and back on at 6pm, and that must not be the
 * same action as unpublishing the product.
 */
export const productFnbSchema = z.object({
  isSoldOut: z.boolean(),
  /** Empty means always on sale; otherwise the item hides outside its hours. */
  isSellingNow: z.boolean(),
  prepMinutes: z.number().int(),
  /**
   * Only what a customer needs to choose with. Not `modifierGroupSchema` —
   * that one carries `position` and `isActive`, which are the merchant's
   * editing concerns and have already been applied by the time a group
   * reaches a storefront.
   */
  modifierGroups: z.array(
    z.object({
      id: z.string().uuid(),
      name: i18nTextSchema,
      selection: modifierSelectionSchema,
      minSelect: z.number().int(),
      maxSelect: z.number().int(),
      options: z.array(
        z.object({
          id: z.string().uuid(),
          name: i18nTextSchema,
          priceDelta: z.number().int(),
          isDefault: z.boolean(),
          isAvailable: z.boolean(),
        }),
      ),
    }),
  ),
});
export type ProductFnbDto = z.infer<typeof productFnbSchema>;

/** Services extension: what the customer needs before they can pick a slot. */
export const productServiceSchema = z.object({
  durationMinutes: z.number().int(),
  bufferMinutes: z.number().int(),
  depositAmount: z.number().int().nullable(),
  resources: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      photoUrl: z.string().nullable(),
    }),
  ),
});
export type ProductServiceDto = z.infer<typeof productServiceSchema>;

export const productSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: i18nTextSchema,
  description: i18nTextSchema,
  categoryId: z.string().uuid().nullable(),
  price: moneySchema,
  compareAtPrice: moneySchema.nullable(),
  images: z.array(imageSchema),
  status: productStatusSchema,
  trackInventory: z.boolean(),
  /** Sum of variant stock, or null when inventory is not tracked. */
  totalStock: z.number().int().nullable(),
  variants: z.array(retailVariantSchema),
  position: z.number().int(),
  /**
   * Per-vertical extensions (§4: base product + extension tables).
   *
   * Both are optional and both are null for a retail product. They are NOT a
   * discriminated union keyed on the shop's vertical, because the storefront
   * renders one component tree for every vertical — a nullable block it can
   * test for is less machinery than a union it would have to narrow at every
   * call site, for exactly two cases.
   */
  fnb: productFnbSchema.nullable().default(null),
  service: productServiceSchema.nullable().default(null),
});
export type ProductDto = z.infer<typeof productSchema>;

/** Trimmed shape for grids — never ship descriptions into a list payload. */
export const productCardSchema = productSchema.pick({
  id: true,
  slug: true,
  name: true,
  price: true,
  compareAtPrice: true,
  images: true,
  totalStock: true,
  categoryId: true,
}).extend({
  /**
   * §4's one-tap sold-out state, surfaced in the grid.
   *
   * A retail card says nothing here and relies on `totalStock`; an F&B card
   * needs its own flag, because a sold-out dish has no stock count to go to
   * zero. Letting a customer tap into an item that cannot be made and only
   * telling them on the detail page is the kind of small rudeness that sends
   * them back to messaging the shop by hand.
   */
  soldOut: z.boolean().default(false),
});
export type ProductCardDto = z.infer<typeof productCardSchema>;

export const upsertVariantSchema = z.object({
  id: z.string().uuid().optional(),
  sku: z.string().max(40).nullable().optional(),
  size: z.string().max(30).nullable().optional(),
  color: z.string().max(30).nullable().optional(),
  colorHex: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  priceAmount: z.number().int().min(0).nullable().optional(),
  stock: z.number().int().min(0).default(0),
  imageUrl: z.string().url().nullable().optional(),
  isActive: z.boolean().default(true),
});

export const upsertProductSchema = z.object({
  name: i18nTextSchema,
  description: i18nTextSchema.optional(),
  categoryId: z.string().uuid().nullable().optional(),
  priceAmount: z.number().int().min(0),
  compareAtPriceAmount: z.number().int().min(0).nullable().optional(),
  currency: currencySchema.optional(),
  images: z.array(imageSchema).max(10).default([]),
  status: productStatusSchema.default('draft'),
  trackInventory: z.boolean().default(true),
  variants: z.array(upsertVariantSchema).max(100).default([]),
});
export type UpsertProductInput = z.infer<typeof upsertProductSchema>;

export const upsertCategorySchema = z.object({
  name: i18nTextSchema,
  position: z.number().int().min(0).default(0),
});

/**
 * Bulk stock edit — the single most-used merchant action in fashion retail,
 * so it gets a first-class endpoint instead of N product updates.
 */
export const adjustStockSchema = z.object({
  adjustments: z
    .array(
      z.object({
        variantId: z.string().uuid(),
        /** Absolute value wins when present; otherwise apply the delta. */
        stock: z.number().int().min(0).optional(),
        delta: z.number().int().optional(),
      }),
    )
    .min(1)
    .max(200),
  note: z.string().max(200).optional(),
});

export const storefrontQuerySchema = z.object({
  categoryId: z.string().uuid().optional(),
  q: z.string().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(48).default(24),
  cursor: z.string().optional(),
  /** Acquisition source (§6.3) — carried into the order's attribution blob. */
  src: z.string().max(60).optional(),
  ref: z.string().max(60).optional(),
});

/**
 * Facebook import (CLAUDE.md §3 `catalog` + §10): paste a post or album link,
 * a vision model extracts name/price/images into DRAFT products the merchant
 * reviews. Drafts are never auto-published.
 */
export const facebookImportRequestSchema = z.object({
  url: z.string().url(),
  /** Optional pasted caption when the link itself is not publicly readable. */
  caption: z.string().max(4000).optional(),
  imageUrls: z.array(z.string().url()).max(20).optional(),
});

export const importedProductDraftSchema = z.object({
  name: i18nTextSchema,
  description: i18nTextSchema,
  priceAmount: z.number().int().min(0).nullable(),
  currency: currencySchema.nullable(),
  images: z.array(z.string().url()).default([]),
  suggestedSizes: z.array(z.string().max(30)).default([]),
  suggestedColors: z.array(z.string().max(30)).default([]),
  confidence: z.number().min(0).max(1).default(0.5),
});
export type ImportedProductDraft = z.infer<typeof importedProductDraftSchema>;

export const facebookImportResultSchema = z.object({
  jobId: z.string().uuid(),
  drafts: z.array(importedProductDraftSchema),
});
